import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useState } from 'react'
import { isEmpty, isEqual, uniqBy } from 'lodash'
import { useMutation } from '@tanstack/react-query'
import { updateBillingContactsOrganizationOrganizationIdUpdateBillingContactsPutMutation } from '@/generated/@tanstack/react-query.gen'
import ContactCard from './billing-contact-card'
import Spinner from '@/components/Spinner'
import { toast as customToast } from '@/components/custom-toast'
import {
  BillingContact,
  billingContactSchema,
} from '@/pages/enterprise/_data/payment-billing-contact-schema'
import { useUser } from '@/pages/enterprise/user-provider'

interface Props {
  onCancel: () => void
  onSuccess: () => void
}

export function UpdateBillingContactsForm({ onCancel, onSuccess }: Props) {
  const { billingContacts, organizationId, onUpdateSeats } = useUser()
  const [billingContactsState, setBillingContactsState] =
    useState<z.infer<typeof billingContactSchema>[]>(billingContacts)

  const form = useForm<z.infer<typeof billingContactSchema>>({
    resolver: zodResolver(
      billingContactSchema
        .optional()
        .refine(
          (data) => {
            const email = data?.email ?? ''
            return isEmpty(email) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
          },
          { path: ['email'], message: 'Invalid email' }
        )
        .refine(
          (data) => {
            const email = data?.email ?? ''
            return (
              isEmpty(email) ||
              !billingContacts.some((contact) => contact.email === email)
            )
          },
          { path: ['email'], message: 'Email already exists' }
        )
    ),

    defaultValues: {
      lastName: '',
      firstName: '',
      email: '',
    },
  })

  const updateBillingContact = useMutation({
    ...updateBillingContactsOrganizationOrganizationIdUpdateBillingContactsPutMutation(),
    onError: () =>
      customToast.error({
        message: 'Failed to add billing contact. Please try again.',
      }),
    onSuccess: () => {
      onUpdateSeats()
      onSuccess()
      customToast.success({
        message: 'Billing contact updated.',
      })
    },
  })

  const onSubmit = (data: z.infer<typeof billingContactSchema>) => {
    setBillingContactsState((prev) => {
      const newContacts = [...prev, data].filter((item) => item.email !== '')
      return uniqBy(newContacts, 'email')
    })
    form.reset()
  }

  const onDelete = (data: z.infer<typeof billingContactSchema>) => {
    setBillingContactsState((prev) => {
      const newContacts = prev.filter((item) => item.email !== data.email)

      return uniqBy(newContacts, 'email')
    })
  }

  const onEdit = ({
    prev,
    next,
  }: {
    prev: BillingContact
    next: BillingContact
  }) => {
    const newContacts = billingContactsState.map((item) =>
      item.email === prev.email ? next : item
    )
    setBillingContactsState(newContacts)
  }

  const onSave = () => {
    updateBillingContact.mutate({
      path: { organization_id: organizationId },
      body: {
        billing_contacts: billingContactsState,
      },
    })
  }

  return updateBillingContact.isPending ? (
    <div>
      <Spinner />
    </div>
  ) : (
    <div className="space-y-4 ">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className=" flex flex-col gap-4 2xl:flex-row md:flex-row">
            <div className="flex-1 grid grid-cols-1  2xl:grid-cols-3 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl className="h-14">
                      <Input
                        placeholder="First name"
                        className="h-14 md:col-span-1"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Last name"
                        className="h-14 md:col-span-1"
                        {...field}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Email"
                        className="h-14 md:col-span-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button className="w-16 h-14" type="submit">
              Add
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-8 text-sm">
            {billingContactsState.map((contact) => (
              <ContactCard
                key={contact.email}
                onDelete={onDelete}
                billingContacts={billingContacts}
                onEdit={(value) => {
                  onEdit({
                    prev: contact,
                    next: value,
                  })
                }}
                billingContact={contact}
              />
            ))}
          </div>
        </form>
      </Form>

      <div className="flex  gap-2">
        <Button
          disabled={
            !isEmpty(form.formState.errors) ||
            isEqual(billingContacts, billingContactsState)
          }
          onClick={onSave}
        >
          Save Changes
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
